import { Card, CardHeader, CardContent } from "./card";

export function TimelineSkeleton() {
  return (
    <div className="max-w-7xl mx-auto py-6 animate-pulse text-left space-y-6">
      <div className="h-8 w-48 bg-muted rounded mb-6" />
      <div className="relative border-l border-border/80 pl-6 ml-4 space-y-12">
        {[1, 2].map((i) => (
          <div key={i} className="relative">
            <span className="absolute left-[-31px] top-1.5 flex h-4 w-4 rounded-full bg-muted border-2 border-muted" />
            <div className="border border-border/60 bg-card/65 rounded-xl p-6 space-y-4 h-32">
              <div className="flex justify-between items-center">
                <div className="h-5 w-24 bg-muted rounded-full" />
                <div className="h-3 w-12 bg-muted rounded" />
              </div>
              <div className="space-y-2 pt-2">
                <div className="h-5 w-1/3 bg-muted rounded" />
                <div className="h-4 w-1/4 bg-muted rounded" />
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export function HomeSkeleton() {
  return (
    <div className="space-y-12 max-w-7xl mx-auto py-6 animate-pulse">
      {/* Intro Section Skeleton */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center text-center lg:text-left">
        <div className="lg:col-span-8 space-y-6 flex flex-col items-center lg:items-start">
          <div className="h-6 w-32 bg-muted rounded-full" />
          <div className="space-y-3 w-full flex flex-col items-center lg:items-start">
            <div className="h-10 md:h-12 w-3/4 bg-muted rounded-xl" />
            <div className="h-10 md:h-12 w-1/2 bg-muted rounded-xl" />
          </div>
          <div className="space-y-2 w-full max-w-3xl flex flex-col items-center lg:items-start">
            <div className="h-4 w-full bg-muted rounded" />
            <div className="h-4 w-5/6 bg-muted rounded" />
            <div className="h-4 w-4/5 bg-muted rounded" />
          </div>
          <div className="flex gap-3 pt-2">
            <div className="h-11 w-32 bg-muted rounded-lg" />
            <div className="h-11 w-36 bg-muted rounded-lg" />
          </div>
        </div>
        <div className="lg:col-span-4 flex justify-center lg:justify-end">
          <div className="w-60 h-60 md:w-64 md:h-64 lg:w-72 lg:h-72 rounded-2xl bg-muted shrink-0" />
        </div>
      </div>

      {/* Highlights Grid Skeleton */}
      <div className="space-y-6 text-left">
        <div className="border-b border-border/80 pb-4 space-y-2">
          <div className="h-6 w-40 bg-muted rounded" />
          <div className="h-4 w-60 bg-muted rounded" />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {[1, 2, 3, 4].map((i) => (
            <Card key={i} className="border border-border/60 bg-card/65 rounded-xl p-5 flex gap-4 h-24">
              <div className="h-10 w-10 shrink-0 bg-muted rounded-xl" />
              <div className="flex-1 space-y-2">
                <div className="h-4 w-20 bg-muted rounded" />
                <div className="h-5 w-3/4 bg-muted rounded" />
              </div>
            </Card>
          ))}
        </div>
      </div>

      {/* Testimonials Section Skeleton */}
      <div className="space-y-6 pt-6 border-t border-border/40 text-left">
        <div className="border-b border-border/80 pb-4 space-y-2">
          <div className="h-6 w-48 bg-muted rounded" />
          <div className="h-4 w-64 bg-muted rounded" />
        </div>
        <Card className="border border-border/60 bg-card/65 rounded-xl p-6 md:p-8 flex flex-col md:flex-row gap-6 items-center md:items-start h-[180px]">
          <div className="h-20 w-20 md:h-24 md:w-24 bg-muted rounded-full shrink-0" />
          <div className="flex-1 space-y-3 w-full">
            <div className="h-5 w-32 bg-muted rounded" />
            <div className="h-4 w-48 bg-muted rounded" />
            <div className="space-y-2 pt-2">
              <div className="h-4 w-full bg-muted rounded" />
              <div className="h-4 w-5/6 bg-muted rounded" />
            </div>
          </div>
        </Card>
        <div className="flex justify-center gap-2 pt-2">
          {[1, 2, 3].map((i) => (
            <div key={i} className="w-8 h-8 flex items-center justify-center">
              <span className={`h-2.5 rounded-full bg-muted-foreground/25 ${i === 1 ? "w-8 bg-muted-foreground/45" : "w-2.5"}`} />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export function HobbySkeleton() {
  return (
    <div className="space-y-12 max-w-7xl mx-auto py-6 animate-pulse text-left">
      <div className="space-y-8">
        <div className="border-b border-border/60 pb-3 space-y-2">
          <div className="h-6 w-48 bg-muted rounded" />
          <div className="h-4 w-64 bg-muted rounded" />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {[1, 2, 3].map((colIdx) => (
            <div key={colIdx} className="space-y-4">
              <div className="flex items-center gap-2">
                <div className="h-8 w-8 bg-muted rounded-lg" />
                <div className="h-4 w-24 bg-muted rounded" />
              </div>
              <div className="flex flex-col gap-3">
                {[1, 2, 3].map((cardIdx) => (
                  <Card key={cardIdx} className="border border-border/40 bg-card/65 rounded-xl p-4 space-y-2 h-[88px]">
                    <div className="h-4 w-3/4 bg-muted rounded" />
                    <div className="h-3 w-1/2 bg-muted rounded" />
                  </Card>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export function CommunitySkeleton() {
  return (
    <div className="space-y-12 max-w-7xl mx-auto py-6 animate-pulse text-left">
      <div className="space-y-4">
        <div className="h-8 w-48 bg-muted rounded" />
        <div className="h-4 w-full max-w-3xl bg-muted rounded" />
        <div className="h-4 w-5/6 max-w-3xl bg-muted rounded" />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-start">
        {/* Left Side: Timeline Skeleton */}
        <div className="space-y-6">
          <div className="h-6 w-48 bg-muted rounded border-b border-border/60 pb-3" />
          <div className="relative border-l border-border/80 pl-6 ml-4 space-y-12">
            {[1, 2].map((i) => (
              <div key={i} className="relative">
                <span className="absolute left-[-31px] top-1.5 flex h-4 w-4 rounded-full bg-muted border-2 border-muted" />
                <div className="border border-border/60 bg-card/65 rounded-xl p-6 space-y-4 h-32">
                  <div className="flex justify-between items-center">
                    <div className="h-5 w-24 bg-muted rounded-full" />
                    <div className="h-3 w-12 bg-muted rounded" />
                  </div>
                  <div className="space-y-2 pt-2">
                    <div className="h-5 w-1/3 bg-muted rounded" />
                    <div className="h-4 w-1/4 bg-muted rounded" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Right Side: Causes Skeleton */}
        <div className="space-y-6">
          <div className="h-6 w-40 bg-muted rounded border-b border-border/60 pb-3" />
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <Card key={i} className="border border-border/60 bg-card/65 rounded-xl p-5 flex gap-4 h-[106px]">
                <div className="h-10 w-10 shrink-0 bg-muted rounded-xl" />
                <div className="flex-1 space-y-2">
                  <div className="h-4 w-32 bg-muted rounded" />
                  <div className="h-3 w-full bg-muted rounded" />
                  <div className="h-3 w-5/6 bg-muted rounded" />
                </div>
              </Card>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export function ContactSkeleton() {
  return (
    <div className="max-w-7xl mx-auto py-6 px-4 space-y-6 animate-pulse">
      <div className="h-10 w-48 bg-muted rounded-lg" />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-stretch pt-2">
        {/* Form Skeleton */}
        <Card className="border border-border/60 bg-card/65 p-6 md:p-8 space-y-6 rounded-xl h-[450px] flex flex-col">
          <div className="space-y-2 text-left">
            <div className="h-6 w-32 bg-muted rounded" />
            <div className="h-4 w-48 bg-muted rounded" />
          </div>
          <div className="space-y-4 pt-4 flex-1 flex flex-col justify-center">
            <div className="h-10 w-full bg-muted rounded" />
            <div className="h-10 w-full bg-muted rounded" />
            <div className="h-28 w-full bg-muted rounded flex-1" />
          </div>
        </Card>

        {/* Socials Skeleton */}
        <Card className="border border-border/60 bg-card/65 p-6 md:p-8 space-y-6 rounded-xl flex flex-col justify-between">
          <div className="space-y-2 text-left">
            <div className="h-6 w-32 bg-muted rounded" />
            <div className="h-4 w-48 bg-muted rounded" />
          </div>
          <CardContent className="p-0 grid grid-cols-2 sm:grid-cols-3 gap-3 pt-4 flex-1">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div key={i} className="flex flex-col items-center justify-center p-4 border border-border/40 rounded-xl bg-card h-24 space-y-2">
                <div className="h-6 w-6 bg-muted rounded-full animate-pulse" />
                <div className="h-3 w-12 bg-muted rounded animate-pulse" />
              </div>
            ))}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

export function ProjectsSkeleton() {
  return (
    <div className="space-y-12 max-w-7xl mx-auto py-6 animate-pulse text-left">
      <div className="space-y-4 max-w-3xl">
        <div className="h-8 w-48 bg-muted rounded" />
        <div className="h-4 w-full bg-muted rounded" />
        <div className="h-4 w-5/6 bg-muted rounded" />
      </div>

      <div className="w-full h-14 border-y border-border/40 bg-card/20 rounded-xl flex items-center px-6 gap-6 overflow-hidden">
        {[1, 2, 3, 4, 5, 6].map((i) => (
          <div key={i} className="h-7 w-20 bg-muted/60 rounded-full shrink-0" />
        ))}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {[1, 2, 3].map((i) => (
          <Card key={i} className="border border-border/60 bg-card/65 rounded-xl p-5 flex flex-col justify-between h-[180px] space-y-4">
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <div className="h-4 w-12 bg-muted rounded" />
                <div className="h-4 w-24 bg-muted rounded" />
              </div>
              <div className="h-6 w-3/4 bg-muted rounded" />
              <div className="space-y-2">
                <div className="h-4 w-full bg-muted rounded" />
                <div className="h-4 w-5/6 bg-muted rounded" />
              </div>
            </div>
            <div className="flex gap-2">
              <div className="h-4 w-10 bg-muted/50 rounded-full" />
              <div className="h-4 w-12 bg-muted/50 rounded-full" />
              <div className="h-4 w-14 bg-muted/50 rounded-full" />
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}

